import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { roleService } from "../../services/role.service";
import { Role } from "../../interfaces/role.interface";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import DataTable, { Column } from "../../components/ui/DataTable";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { formatPermission } from "../../utils/permission-formatter";
import { getErrorMsg } from "../../utils/error";
import { useConfirm } from '../../hooks/useConfirm';

const RolesList = () => {
  const { t } = useTranslation("common");
  const { confirm, ConfirmDialog } = useConfirm();
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
  });

  const canCreate = hasPermission(Permission.ROLES_CREATE);
  const canUpdate = hasPermission(Permission.ROLES_UPDATE);
  const canDelete = hasPermission(Permission.ROLES_DELETE);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await roleService.findAll();
      setRoles(data);
    } catch (e) {
      setError(getErrorMsg(e, "Failed to load roles"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setCurrentRole(role);
      setFormData({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions || [],
      });
    } else {
      setCurrentRole(null);
      setFormData({
        name: "",
        description: "",
        permissions: [],
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (currentRole) {
        await roleService.update(currentRole.id, {
          description: formData.description,
          permissions: formData.permissions as Permission[],
        });
      } else {
        await roleService.create({
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions as Permission[],
        });
      }
      setIsDialogOpen(false);
      refresh();
    } catch (e) {
      setError(getErrorMsg(e, "Failed to save role"));
    }
  };

  const handleDelete = async (id: string) => {
    if (!await confirm(t('confirmDelete'))) return;
    try {
      await roleService.remove(id);
      refresh();
    } catch (e) {
      setError(getErrorMsg(e, "Failed to delete role"));
    }
  };

  const togglePermission = (permission: string) => {
    setFormData((prev) => {
      const exists = prev.permissions.includes(permission);
      if (exists) {
        return {
          ...prev,
          permissions: prev.permissions.filter((p) => p !== permission),
        };
      } else {
        return {
          ...prev,
          permissions: [...prev.permissions, permission],
        };
      }
    });
  };

  const groupPermissions = (permissions: string[]) => {
    const groups: Record<string, string[]> = {};
    permissions.forEach((p) => {
      const [group] = p.split(".");
      if (!groups[group]) groups[group] = [];
      groups[group].push(p);
    });
    return groups;
  };

  const allPermissions = Object.values(Permission);
  const permissionGroups = groupPermissions(allPermissions);

  const columns: Column<Role>[] = [
    {
      key: "name",
      header: "Name",
      className: "font-medium text-gray-900 dark:text-gray-100",
    },
    {
      key: "description",
      header: "Description",
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (role) => (
        <div className="space-x-2">
          {canUpdate && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleOpenDialog(role)}
            >
              Edit
            </Button>
          )}
          {canDelete && !role.isSystem && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDelete(role.id)}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout title={t("roles")}>
      <div className="space-y-4 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("roles")}
            </h1>
            {canCreate && (
              <div className="flex items-center gap-2">
                <Button onClick={() => handleOpenDialog()} variant="primary">
                  + {t("roles")}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">{error}</div>
        )}

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          data={roles}
          loading={loading}
          emptyMessage="No roles found"
        />

        <Dialog
          open={isDialogOpen}
          title={currentRole ? "Edit Role" : "Create Role"}
          onClose={() => setIsDialogOpen(false)}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              disabled={!!currentRole} // Name cannot be changed for existing roles (for now)
              required
            />
            <Input
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  description: e.target.value,
                })
              }
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto border p-4 rounded-md dark:border-gray-700">
                {Object.entries(permissionGroups).map(([group, perms]) => (
                  <div key={group} className="mb-2">
                    <h4 className="font-semibold capitalize mb-1 text-gray-800 dark:text-gray-200">
                      {group.replace(/_/g, " ")}
                    </h4>
                    <div className="space-y-1 ml-2">
                      {perms.map((perm) => (
                        <label
                          key={perm}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm)}
                            onChange={() => togglePermission(perm)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {formatPermission(perm)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </div>
          </form>
        </Dialog>
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default RolesList;
